export type CarePlanPhaseLabel = {
  labelKey: string;
  fallback: string;
  values?: { label: string };
};

const PHASE_LABELS: Record<string, CarePlanPhaseLabel> = {
  PostConsent: {
    labelKey: 'carePlan.phase.postConsent',
    fallback: 'After consent',
  },
  EventRelativePre: {
    labelKey: 'carePlan.phase.eventRelativePre',
    fallback: 'Before event',
  },
  EventRelativePost: {
    labelKey: 'carePlan.phase.eventRelativePost',
    fallback: 'After event',
  },
};

const NAMED_EVENT_LABELS: Record<string, CarePlanPhaseLabel> = {
  EventRelativePre: {
    labelKey: 'carePlan.phase.eventRelativePreNamed',
    fallback: 'Before {{label}}',
  },
  EventRelativePost: {
    labelKey: 'carePlan.phase.eventRelativePostNamed',
    fallback: 'After {{label}}',
  },
};

/**
 * Translation key + fallback for a care-plan scheduling phase
 * (PostConsent / EventRelativePre / EventRelativePost), reusing the same
 * carePlan.phase.* labels as the admin care-plan configuration. Null when
 * the task carries no phase or an unrecognised value.
 *
 * When the plan defines an event label (e.g. "Surgery date"), the
 * event-relative phases personalise the wording to "After/Before <label>"
 * via the *Named keys and a {{label}} interpolation value (AMI-677). An
 * absent or blank event label keeps the generic wording.
 */
export const getCarePlanPhaseLabel = (
  phase: string | null | undefined,
  eventLabel?: string | null,
): CarePlanPhaseLabel | null => {
  if (!phase) return null;

  const named = NAMED_EVENT_LABELS[phase];
  const trimmedLabel = eventLabel?.trim();
  if (named && trimmedLabel) 
    return { ...named, values: { label: trimmedLabel } };

  return PHASE_LABELS[phase] ?? null;
};
